/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";

import { Listbox } from "@headlessui/react";

import Arrow from "./icons/arrow";

export interface option {
  id: string;
  title: string;
  [key: string]: any;
}

interface DropDownButtonProps {
  customIcon?: React.ReactNode;
  buttonTitle: string;
  listOptions?: option[];
  listOption?: option;
  setListOption?: React.Dispatch<React.SetStateAction<option>>;
  customDropdownPanel?: React.ReactNode;
  fullWidth?: boolean;
  listButtonCustomClasses?: string;
}

const DropDownButton: React.FC<DropDownButtonProps> = ({
  customIcon,
  buttonTitle,
  listOptions,
  listOption,
  setListOption,
  customDropdownPanel,
  fullWidth = false,
  listButtonCustomClasses,
}) => {
  return (
    <div
      className={`relative ${fullWidth ? "!max-w-none" : "max-w-[121px] md:max-w-[250px]"} w-full`}
    >
      <Listbox value={listOption} onChange={setListOption}>
        {({ open }) => (
          <>
            <ListboxButton
              open={open}
              buttonTitle={buttonTitle}
              listOptionTitle={listOption?.title || ""}
              customIcon={customIcon}
              listButtonCustomClasses={listButtonCustomClasses}
            />

            {((listOptions && listOptions?.length > 0) || customDropdownPanel) && (
              <Listbox.Options
                className={`p-2 absolute top-full left-0 rtl:left-auto right-auto  z-[1000] border border-neutral-400 rounded space-y-1 bg-white ${
                  customDropdownPanel
                    ? "w-full !max-w-[200px]"
                    : "min-w-fit w-full border-neutral-300 p-2 space-y-1"
                } mt-1 animate-slide-in !overflow-y-auto overflow-x-hidden max-h-[300px] h-md:max-h-[400px]`}
                style={{
                  boxShadow: "0px 24px 48px -12px #1018282E",
                }}
              >
                {customDropdownPanel
                  ? customDropdownPanel
                  : listOptions?.map((option, index: number) => (
                      <Listbox.Option
                        key={option?.id || index}
                        value={{
                          ...option,
                          id: option?.id ?? "",
                          title: option?.title ?? "",
                        }}
                      >
                        {({ active }) => {
                          return (
                            <div
                              className={`w-full max-w-[200px] flex justify-between gap-2 items-center truncate p-3 text-primary-1000 text-sm hover:!bg-neutral-100 rounded cursor-pointer transition-colors  ${
                                active || option?.id === listOption?.id ? " bg-neutral-200" : ""
                              }`}
                            >
                              {option?.title}

                              {option?.id === listOption?.id ? (
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 16 17"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg" aria-hidden={true} focusable="false">
                                  <path
                                    d="M0 2.5C0 1.39543 0.895431 0.5 2 0.5H14C15.1046 0.5 16 1.39543 16 2.5V14.5C16 15.6046 15.1046 16.5 14 16.5H2C0.895431 16.5 0 15.6046 0 14.5V2.5Z"
                                    fill="transparent"
                                  />
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M11.7296 5.5233C12.0901 5.88378 12.0901 6.46825 11.7296 6.82874L7.49887 11.0596C7.32576 11.2327 7.09097 11.3299 6.84615 11.3299C6.60134 11.3299 6.36655 11.2327 6.19344 11.0596L4.27036 9.13646C3.90988 8.77597 3.90988 8.1915 4.27036 7.83101C4.63085 7.47053 5.21531 7.47053 5.57579 7.83101L6.84615 9.10139L10.4242 5.5233C10.7847 5.16281 11.3692 5.16281 11.7296 5.5233Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              ) : (
                                ""
                              )}
                            </div>
                          );
                        }}
                      </Listbox.Option>
                    ))}
              </Listbox.Options>
            )}
          </>
        )}
      </Listbox>
    </div>
  );
};

export default React.memo(DropDownButton);

interface ListboxButtonProps {
  open: boolean;
  buttonTitle: string;
  listOptionTitle: string;
  customIcon?: React.ReactNode;
  listButtonCustomClasses?: string;
}

const ListboxButton: React.FC<ListboxButtonProps> = ({
  open,
  buttonTitle,
  listOptionTitle,
  customIcon,
  listButtonCustomClasses,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!open && buttonRef.current) {
      buttonRef.current.blur();
    }
  }, [open]);

  return (
    <Listbox.Button
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      className={`
                relative flex items-center justify-between gap-2
                w-full px-5 py-[5px] rounded
                overflow-hidden !bg-white transition-colors
                  active:bg-neutral-200

                after:content-[''] after:absolute after:bottom-0 after:left-1/2
                after:w-full after:h-0.5 after:bg-neutral-800
                after:origin-center after:transition-transform after:duration-300
                after:ease-in-out after:rounded-b-sm after:pointer-events-none
                after:-translate-x-1/2
                border border-neutral-400 hover:border-neutral-500

                ${
                  open
                    ? "!bg-transparent border-neutral-400 after:scale-x-100 "
                    : "bg-neutral-100 after:scale-x-0"
                }
                ${!open && isFocused ? "after:scale-x-100" : ""}
                ${listButtonCustomClasses || ""}
            `}
    >
      <span className="text-xs md:text-sm text-gray-900 truncate">
        {buttonTitle && !listOptionTitle ? buttonTitle : listOptionTitle}
      </span>
      {customIcon ? (
        customIcon
      ) : (
        <Arrow
          className={`min-w-[20px] min-h-[20px] md:min-w-[24px] md:min-h-[24px] ${
            open ? "rotate-180" : ""
          } transition-transform`}
          aria-label=""
          aria-hidden={true}
        />
      )}
    </Listbox.Button>
  );
};
